import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import "@/App.css";
import { AuthProvider } from "@/context/AuthContext";
import { AccountProvider } from "@/context/AccountContext";
import { AppLayout } from "@/components/AppLayout";
import { ProtectedRoute, PublicOnlyRoute } from "@/components/RouteGuards";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import VerifyEmail from "@/pages/VerifyEmail";
import Dashboard from "@/pages/Dashboard";
import Signals from "@/pages/Signals";
import Connect from "@/pages/Connect";
import Journal from "@/pages/Journal";
import Scanner from "@/pages/Scanner";
import Portfolio from "@/pages/Portfolio";
import Risk from "@/pages/Risk";
import Strategies from "@/pages/Strategies";
import Analytics from "@/pages/Analytics";
import Alerts from "@/pages/Alerts";
import PropChallenge from "@/pages/PropChallenge";
import PropChallengeIndex from "@/pages/PropChallengeIndex";
import Backtest from "@/pages/Backtest";
import Leaderboard from "@/pages/Leaderboard";
import Straddles from "@/pages/Straddles";
import Settings from "@/pages/Settings";
import AutonomousIntelligence from "@/pages/AutonomousIntelligence";
import HealthScore from "@/pages/HealthScore";
import Coach from "@/pages/Coach";
import Optimizer from "@/pages/Optimizer";
import Prediction from "@/pages/Prediction";
// Phase 8A — Admin surface
import { AdminLayout } from "@/components/AdminLayout";
import RequireAdmin from "@/components/RequireAdmin";
import AdminOverview from "@/pages/admin/AdminOverview";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminUserDetail from "@/pages/admin/AdminUserDetail";
import AdminAccounts from "@/pages/admin/AdminAccounts";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";
import AdminLLMUsage from "@/pages/admin/AdminLLMUsage";
import AdminImpersonationLog from "@/pages/admin/AdminImpersonationLog";

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AccountProvider>
            <Routes>
              <Route path="/login" element={<PublicOnlyRoute><Login /></PublicOnlyRoute>} />
              <Route path="/register" element={<PublicOnlyRoute><Register /></PublicOnlyRoute>} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              {/* /leaderboard is PUBLIC — accessible without login. */}
              <Route path="/leaderboard" element={<Leaderboard />} />
              <Route
                element={
                  <ProtectedRoute>
                    <AppLayout />
                  </ProtectedRoute>
                }
              >
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/ati" element={<AutonomousIntelligence />} />
                <Route path="/health-score" element={<HealthScore />} />
                <Route path="/coach" element={<Coach />} />
                <Route path="/optimizer" element={<Optimizer />} />
                <Route path="/prediction" element={<Prediction />} />
                <Route path="/scanner" element={<Scanner />} />
                <Route path="/signals" element={<Signals />} />
                <Route path="/straddles" element={<Straddles />} />
                <Route path="/portfolio" element={<Portfolio />} />
                <Route path="/analytics" element={<Analytics />} />
                <Route path="/risk" element={<Risk />} />
                <Route path="/strategies" element={<Strategies />} />
                <Route path="/accounts" element={<Navigate to="/connect" replace />} />
                <Route path="/backtest" element={<Backtest />} />
                <Route path="/prop-challenge" element={<PropChallengeIndex />} />
                <Route path="/prop-challenge/:account_id" element={<PropChallenge />} />
                <Route path="/alerts" element={<RequireAdmin><Alerts /></RequireAdmin>} />
                <Route path="/connect" element={<RequireAdmin><Connect /></RequireAdmin>} />
                <Route path="/connect-ea" element={<Navigate to="/connect" replace />} />                <Route path="/journal" element={<Journal />} />
                <Route path="/settings" element={<RequireAdmin><Settings /></RequireAdmin>} />
                <Route path="/settings/api-keys" element={<RequireAdmin><Settings /></RequireAdmin>} />
              </Route>

              {/* Phase 8A — Admin routes (RequireAdmin guards non-admins) */}
              <Route
                element={
                  <RequireAdmin>
                    <AdminLayout />
                  </RequireAdmin>
                }
              >
                <Route path="/admin" element={<AdminOverview />} />
                <Route path="/admin/users" element={<AdminUsers />} />
                <Route path="/admin/users/:user_id" element={<AdminUserDetail />} />
                <Route path="/admin/accounts" element={<AdminAccounts />} />
                <Route path="/admin/analytics" element={<AdminAnalytics />} />
                <Route path="/admin/llm-usage" element={<AdminLLMUsage />} />
                <Route path="/admin/impersonation-log" element={<AdminImpersonationLog />} />
              </Route>
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </AccountProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
